/**
 * Modulo Events
 * @module abstracts/events
 * @namespace Abstracts/Events
 */
import Core from '../core'
import _ from 'underscore'
import Backbone from 'backbone'
import UUID from '../../libs/uuid'

/**
 * Classe responsável por definir e disparar eventos
 *
 * @name Events
 * @typedef {Object} Events
 * @memberof abstracts/console
 */
class Events {
    /**
     * Configura a classe eventos
     *
     * @return {void}
     */
    constructor () {
        // Clone da classe Dispatcher do Backbone
        this.Dispatcher = _.clone(Backbone.Events)

        // Eventos padrões imitidos pelos modulos abstratos
        // Conceitualmente chamados de Marcos do Sistema

        // CORE
        this.CORE_FINISHED = 'once-core-finished'

        // PAGE
        this.PAGE_LOADING = 'once-page-loading'
        this.PAGE_READY = 'once-page-ready'
        this.PAGE_COMPLETE = 'once-page-compĺete'
        this.PAGE_SCROLL = 'page-scroll'
        this.PAGE_FOCUS = 'page-focus'
        this.PAGE_BLUR = 'page-blur'
        this.PAGE_FIRST_SCROLL = 'page-first-scroll'
        this.PAGE_SECOND_SCROLL = 'page-second-scroll'
        this.PAGE_SCROLL_UP = 'page-scroll-up'
        this.PAGE_SCROLL_DOWN = 'page-scroll-down'
        this.PAGE_BOTTOM = 'page-bottom'

        // CONTENT
        this.CHANGE_CONTENT = 'change-content'

        // STATUS
        this.CHANGE_STATUS = 'change-status'

        // Lazyloading
        this.SHOW_ELEMENT = 'show-element'
        this.VIEW_ELEMENT = 'view-element'

        // Propriedades privadas
        this._evsArr = []
        this._onceEvsArr = []
        this._entitiesEvsArr = []
        this._eventsSentArr = []

        // Monta um array com os eventos prédefinidos
        this._setPredefineEvsArr()

        // Adiciona um listernes para qualquer evento disparado
        this.Dispatcher.on('all', this._addEventsSent, this)
    }

    /**
     * Inicializa a classe Events
     *
     * @param {any} waiting
     * @param {function} callback
     * @return {void}
     */
    init () {
        // Adiciona um listerner para quando um estado de uma entidade é atualizado
        Core.Status.on('update', this._listenChangeStatus, this)

        Core.Status.update('events', 'status', 'started')

        Core.Status.update('events', 'status', 'finished')
    }

    /**
     * Registra um novo evento
     *
     * @param {string} event - nome do evento Ex.: USER_LOGGED
     * @param {boolean} once - informa se é um evento disparado apenas uma vez
     * @return {void}
     */
    registerEvent (event, once = false) {
        // Registra o nome do Evento
        this[event] = this._prepareEventName(event, once)

        // Adiciona-o no array de eventos pré-definidos
        this._evsArr.push(this[event])

        // Adiciona-o no array de eventos pré-definidos
        if (this[event].indexOf('once') !== -1) {
            this._onceEvsArr.push(this._prepareEventName(event, once))
        }
    }

    /**
     * Cadastra um novo listerner para um evento
     *
     * @param {any} waiting
     * @param {function} callback
     * @return {void}
     */
    on (waiting, callback) {
        try {
            if (!waiting) {
                throw new Error('Waiting não enviado')
            }

            // Verifica se o waiting é um evento predefinido
            if (this._evsArr.indexOf(waiting) !== -1) {
                this._addPredefinedEv(waiting, callback)
                return
            }

            // Se não for será um array de entidades esperando por finished
            this._addEntitiesEv(waiting, callback)

            return
        } catch (ex) {
            console.error('Events', ex)
        }
    }

    /**
     * Verifica se é um evento executa apenas uma vez
     *
     * @params {string} event
     * @return {boolean}
     */
    isOnceEvent (event) {
        if (!event) {
            return true
        }

        if (Array.isArray(event)) {
            return true
        }

        if (this._onceEvsArr.indexOf(event) !== -1) {
            return true
        }

        return false
    }

    /**
     * Define o listerner de um evento prédefinido
     *
     * @params {string} event, {function} callback
     * @return {void}
     */
    _addPredefinedEv (event, callback) {
        // Eventos predefinidos que rodam apenas uma vez
        // Verifica se está na lista de eventos enviados
        if (this._onceEvsArr.indexOf(event) !== -1 &&
           this._eventsSentArr.indexOf(event) !== -1
        ) {
            callback()
            return
        }
        this.Dispatcher.on(event, callback)
    }

    /**
     * Define o listerner para um evento de entidades
     *
     * @param {string|array} entities
     * @param {function} callback
     * @return {void}
     */
    _addEntitiesEv (entities, callback) {
        try {
            entities = this._validateEntitiesEv(entities, callback)

            // Verifica se a entidades já estão finalizadas
            if (this._checkEntitiesStatus(entities)) {
                callback()
                return
            }

            // Se não declara um novo evento
            const evHash = UUID()

            this._entitiesEvsArr.push({ entities, evHash, finished: false })

            this.Dispatcher.on(evHash, callback)
        } catch (ex) {
            console.error('Events', ex)
        }
    }

    /**
     * Método listerner para alteração de um estado de uma entidade
     * Dispara o evento de callback para quando as entidades definidas forem finalizads
     *
     * @return {void}
     */
    _listenChangeStatus () {
        try {
            const actives = this._entitiesEvsArr.filter((ev) => {
                if (!ev.finished) {
                    return ev
                }
            })

            for (const event of actives) {
                if (this._checkEntitiesStatus(event.entities)) {
                    event.finished = true
                    this.Dispatcher.trigger(event.evHash)
                }
            }
        } catch (ex) {
            console.error('Events', ex)
        }
    }

    /**
     * Verifica se a lista de entidades estão finalizadas
     *
     * @param {array} entities
     * @return {boolean}
     */
    _checkEntitiesStatus (entities) {
        let finished = true
        for (const entity of entities) {
            if (Core.Status.get(entity, 'status') !== 'finished') {
                finished = false
            }
        }

        return finished
    }

    /**
     * Valida os eventos de entidades
     *
     * @param {string|array} entities
     * @param {function} callback
     * @return {void}
     */
    _validateEntitiesEv (entities, callback) {
        const errors = []

        if (typeof entities === 'string') {
            entities = [entities]
        }

        if (!Array.isArray(entities) || entities.length === 0) {
            errors.push('Entities é obrigatório deve ser uma string ou um array de strings')
        }

        if (typeof callback !== 'function') {
            errors.push('Callback é obrigatório e deve ser uma função')
        }

        if (errors.length !== 0) {
            throw new Error(errors)
        }

        return entities
    }

    /**
     * Monta um array com todos os eventos pré-definidos
     *
     * @return {void}
     */
    _setPredefineEvsArr () {
        for (const attr of Object.keys(this)) {
            if (typeof this[attr] === 'string') {
                this._evsArr.push(this[attr])
            }

            if (typeof this[attr] === 'string' && this[attr].indexOf('once') !== -1) {
                this._onceEvsArr.push(this[attr])
            }
        }
    }

    /**
     * Listerner de todos eventos
     * Adiciona a lista de eventos enviados
     *
     * @param {any} Evento
     * @return {void}
     */
    _addEventsSent (ev) {
        // console.info('Events', 'Evento Enviado', ev)
        this._eventsSentArr.push(ev)
    }

    /**
     * Prepara o nome do evento
     *
     * @param {any} Evento
     * @return {void}
     */
    _prepareEventName (event, once = false) {
        event = event.toLowerCase()
        event = event.replace(/_/g, '-')
        return `${(once) ? 'once-' : ''}${event}`
    }
}

window.GPAbstractModules = window.GPAbstractModules || {}
window.GPAbstractModules.Events = Events
